import {settleExistingSection} from '../../core/actionHighlighter';

/**
 * The IO-Pass tab each section opens, by the label Canvas renders.
 *
 * `ioPass.jsx` marks its tabs with `data-agent-section` and `aria-selected`
 * the way `Maintenance.jsx` does, so this needs no event and the page needs
 * no listener. The labels are the tab labels themselves - "Configuration"
 * and "Sessions" - because that is what the attribute carries; the resources
 * are named `guest_config` and `client_sessions` after what the tabs hold,
 * which is what a person asking about guest access would say.
 */
const TAB_LABEL = {
	'iopass.guest_config': 'Configuration',
	'iopass.client_sessions': 'Sessions',
};

// The accordions inside Configuration. Reached by heading the same way
// the tabs are reached by label - `expandExistingSection` opens an
// accordion, so the tab has to be current first, which is why each of
// these opens the tab before expanding.
const ACCORDION_HEADING = {
	'iopass.captive_portals': 'Captive Portals',
	'iopass.guest_plans': 'Guest Access Plans',
};

/**
 * Open one IO-Pass tab and settle once it is the one showing.
 * @param {string} sectionId agent section id
 * @return {Promise<void>} resolves once the tab is current
 */
async function openIoPassTab(sectionId) {
	const label = TAB_LABEL[sectionId];
	if (!label) throw new Error('iopass_tab_unknown');
	try {
		// Only what the DOM check establishes: this tab is the one
		// showing. The section id came from the command, so reporting it
		// back as what was seen would dress an echo as an observation.
		return await settleExistingSection(label);
	} catch (_) {
		throw new Error(`${sectionId.replace('.', '_')}_unavailable`);
	}
}

/**
 * Open the IO-Pass Configuration tab.
 * @return {Promise<void>} completion
 */
export function openGuestConfigSection() {
	return openIoPassTab('iopass.guest_config');
}

/**
 * Open the IO-Pass Sessions tab.
 * @return {Promise<void>} completion
 */
export function openClientSessionsSection() {
	return openIoPassTab('iopass.client_sessions');
}

/**
 * Open one accordion on the IO-Pass Configuration tab.
 *
 * Two steps, because the accordion is not in the DOM until its tab is: the
 * Configuration tab settles first, then the heading.
 *
 * @param {string} sectionId agent section id
 * @return {Promise<void>} resolves once the accordion is open
 */
async function openConfigAccordion(sectionId) {
	const heading = ACCORDION_HEADING[sectionId];
	if (!heading) throw new Error('iopass_accordion_unknown');
	await openIoPassTab('iopass.guest_config');
	try {
		return await settleExistingSection(heading);
	} catch (_) {
		throw new Error(`${sectionId.replace('.', '_')}_unavailable`);
	}
}

/**
 * Open the Captive Portals accordion.
 * @return {Promise<void>} completion
 */
export function openCaptivePortalsSection() {
	return openConfigAccordion('iopass.captive_portals');
}

/**
 * Open the Guest Access Plans accordion.
 * @return {Promise<void>} completion
 */
export function openGuestPlansSection() {
	return openConfigAccordion('iopass.guest_plans');
}
